import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { fetchGroups, fetchGroupStudents } from "@/api/groups";
import { fetchLessonByDate, saveLessonAttendance } from "@/api/journal";
import { fetchTeacherDashboard } from "@/api/dashboard";
import { fetchPublicSettings } from "@/api/settings";
import { ATTENDANCE_MARKABLE, ATTENDANCE_STATUS_LABELS, allowsGrade } from "@/types/enums";
import type { AttendanceStatus } from "@/types/enums";
import type { AttendanceRow } from "@/types/group";
import { extractErrorMessage } from "@/types/common";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function JournalPage() {
  const { user, hasPermission } = useAuth();
  const canSeeAll = hasPermission("attendance.all");

  const { data: teacherDashboard } = useQuery({
    queryKey: ["teacher-dashboard", user?.teacherId],
    queryFn: fetchTeacherDashboard,
    enabled: Boolean(user?.teacherId),
  });
  const { data: publicSettings } = useQuery({ queryKey: ["public-settings"], queryFn: fetchPublicSettings });
  const gradeScale = publicSettings?.gradeScale ?? 5;

  const { data: groupsPage } = useQuery({
    queryKey: ["groups", "journal", user?.teacherId, canSeeAll],
    queryFn: () =>
      fetchGroups({
        status: "ACTIVE",
        limit: 200,
        teacherId: !canSeeAll && user?.teacherId ? user.teacherId : undefined,
      }),
  });

  const [searchParams] = useSearchParams();
  const [groupId, setGroupId] = useState(searchParams.get("groupId") ?? "");
  const [date, setDate] = useState(searchParams.get("date") ?? new Date().toISOString().slice(0, 10));
  const [topic, setTopic] = useState("");
  const [homework, setHomework] = useState("");
  const [canceled, setCanceled] = useState(false);
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const groups = groupsPage?.items ?? [];
  useEffect(() => {
    if (!groupId && groups.length > 0) setGroupId(groups[0].id);
  }, [groups, groupId]);

  const { data: students } = useQuery({
    queryKey: ["group-students", groupId],
    queryFn: () => fetchGroupStudents(groupId),
    enabled: Boolean(groupId),
  });

  const { data: existingLesson, isLoading: lessonLoading } = useQuery({
    queryKey: ["lesson", groupId, date],
    queryFn: () => fetchLessonByDate(groupId, date),
    enabled: Boolean(groupId && date),
  });

  useEffect(() => {
    if (!students) return;
    if (existingLesson) {
      setTopic(existingLesson.topic ?? "");
      setHomework(existingLesson.homework ?? "");
      setCanceled(existingLesson.canceled);
      setRows(
        students.map((student) => {
          const record = existingLesson.attendanceRecords?.find((r) => r.studentId === student.id);
          return {
            studentId: student.id,
            studentName: student.fullName,
            status: record?.status ?? "PRESENT",
            grade: record?.grade ?? null,
            homeworkDone: record?.homeworkDone ?? null,
            comment: record?.comment ?? "",
          };
        }),
      );
    } else {
      setTopic("");
      setHomework("");
      setCanceled(false);
      setRows(
        students.map((student) => ({
          studentId: student.id,
          studentName: student.fullName,
          status: "PRESENT" as AttendanceStatus,
          grade: null,
          homeworkDone: null,
          comment: "",
        })),
      );
    }
  }, [students, existingLesson]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      saveLessonAttendance({
        groupId,
        date,
        topic: topic || undefined,
        homework: homework || undefined,
        canceled,
        records: rows.map((r) => ({
          studentId: r.studentId,
          status: r.status,
          grade: r.grade,
          homeworkDone: r.homeworkDone,
          comment: r.comment || undefined,
        })),
      }),
    onSuccess: () => {
      setSnackbar("Журнал сохранён.");
      void queryClient.invalidateQueries({ queryKey: ["lesson", groupId, date] });
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  function updateRow(studentId: string, patch: Partial<AttendanceRow>) {
    setRows((prev) => prev.map((row) => (row.studentId === studentId ? { ...row, ...patch } : row)));
  }

  const gradeOptions = useMemo(() => Array.from({ length: gradeScale }, (_, i) => gradeScale - i), [gradeScale]);

  return (
    <>
      <PageHeader title="Журнал" subtitle="Отметка посещаемости, оценок и домашних заданий" />

      {teacherDashboard && (
        <Paper className="crm-page-filter" variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle2">Уроки на сегодня</Typography>
            <Typography variant="caption" color="text.secondary">
              {teacherDashboard.todayLessons.filter((l) => l.marked).length} из {teacherDashboard.todayLessons.length} отмечено
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {teacherDashboard.todayLessons.map((lesson) => (
              <Stack key={lesson.id} direction="row" spacing={1.5} alignItems="center">
                <StatusChip label={lesson.timeSlot} />
                <Stack flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {lesson.groupCode} · {lesson.subjectName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {lesson.roomName} · {lesson.studentsCount} учеников
                  </Typography>
                </Stack>
                <StatusChip label={lesson.marked ? "Отмечен" : "Не отмечен"} />
                <Button size="small" variant={lesson.marked ? "outlined" : "contained"} onClick={() => { setGroupId(lesson.groupId); setDate(todayIso()); }}>
                  {lesson.marked ? "Изменить" : "Отметить"}
                </Button>
              </Stack>
            ))}
            {teacherDashboard.todayLessons.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Сегодня уроков нет.
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      <Paper className="crm-page-filter crm-journal__lesson" variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField select label="Группа" size="small" value={groupId} onChange={(e) => setGroupId(e.target.value)} sx={{ minWidth: 220 }}>
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.code} — {g.subjectName ?? g.subjectId}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Дата занятия"
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField label="Тема урока" size="small" value={topic} onChange={(e) => setTopic(e.target.value)} sx={{ minWidth: 220 }} />
          <TextField label="Домашнее задание" size="small" value={homework} onChange={(e) => setHomework(e.target.value)} sx={{ minWidth: 220 }} />
          <FormControlLabel
            control={<Checkbox checked={canceled} onChange={(e) => setCanceled(e.target.checked)} />}
            label="Урок отменён"
          />
        </Stack>
      </Paper>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      <Paper className="crm-journal__roster" variant="outlined">
        <QueryState isLoading={lessonLoading && Boolean(groupId)} error={null}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ученик</TableCell>
                <TableCell width={160}>Посещаемость</TableCell>
                <TableCell width={100}>Оценка</TableCell>
                <TableCell width={110}>ДЗ сдано</TableCell>
                <TableCell>Комментарий</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.studentId}>
                  <TableCell>{row.studentName}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={row.status}
                      disabled={canceled}
                      onChange={(e) => {
                        const status = e.target.value as AttendanceStatus;
                        updateRow(row.studentId, { status, ...(allowsGrade(status) ? {} : { grade: null }) });
                      }}
                    >
                      {ATTENDANCE_MARKABLE.map((key) => (
                        <MenuItem key={key} value={key}>
                          {ATTENDANCE_STATUS_LABELS[key]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={row.grade ?? ""}
                      disabled={canceled || !allowsGrade(row.status)}
                      onChange={(e) => updateRow(row.studentId, { grade: e.target.value ? Number(e.target.value) : null })}
                    >
                      <MenuItem value="">—</MenuItem>
                      {gradeOptions.map((g) => (
                        <MenuItem key={g} value={g}>
                          {g}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={row.homeworkDone === null ? "" : row.homeworkDone ? "1" : "0"}
                      disabled={canceled}
                      onChange={(e) => updateRow(row.studentId, { homeworkDone: e.target.value === "" ? null : e.target.value === "1" })}
                    >
                      <MenuItem value="">ДЗ —</MenuItem>
                      <MenuItem value="1">ДЗ сдал</MenuItem>
                      <MenuItem value="0">Не сдал</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.comment}
                      disabled={canceled}
                      onChange={(e) => updateRow(row.studentId, { comment: e.target.value })}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    В выбранной группе нет учеников.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </QueryState>
        <Stack className="crm-table-footer" direction="row" justifyContent="flex-end" p={2}>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={!groupId || mutation.isPending} onClick={() => mutation.mutate()}>
            Сохранить журнал
          </Button>
        </Stack>
      </Paper>

      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar} />
    </>
  );
}
